'use client'

import { ProductCarousel } from '@/components/common'
import { ProductCarouselSkeleton } from '@/components/common/product-carousel/product-carousel-skeleton'
import { __cCollection2Service } from '@/services/client/__c.collection.service'
import type { ProductFormatted } from '@/types/product'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { HomeSectionHeader } from '../home-section-header'
import {
  PRODUCT_TAB_LIMIT,
  buildCollectionHref,
  buildProductTabQuery,
  type TProductTabConfig,
} from './product-tabs.constants'

type Props = {
  title: string
  slug: string
  sort?: string
  tabs: TProductTabConfig[]
  initialProducts: ProductFormatted[]
  viewAllLabel: string
  gaTrackingPrefix: string
}

export const ProductTabsContent = ({
  title,
  slug,
  sort,
  tabs,
  initialProducts,
  viewAllLabel,
  gaTrackingPrefix,
}: Props) => {
  const [activeTab, setActiveTab] = useState(tabs[0])
  const isFirstTab = activeTab.key === tabs[0].key

  // Tab đầu đã có dữ liệu từ server, các tab sau chỉ gọi API khi được chọn
  const { data: tabProducts, isLoading } = useQuery({
    queryKey: ['home-product-tab', slug, sort, activeTab.gender ?? 'all'],
    queryFn: async () => {
      const data = await __cCollection2Service.getCollectionData(slug, {
        ...buildProductTabQuery(sort, activeTab.gender),
        limit: PRODUCT_TAB_LIMIT,
      })
      return data.products
    },
    enabled: !isFirstTab,
    staleTime: 5 * 60 * 1000,
  })

  const products = isFirstTab ? initialProducts : (tabProducts ?? [])

  const handleChangeTab = (key: string) => {
    const tab = tabs.find((item) => item.key === key)
    if (tab) setActiveTab(tab)
  }

  return (
    <div className='space-y-5 lg:space-y-10'>
      <HomeSectionHeader
        title={title}
        tabs={tabs.length > 1 ? tabs : undefined}
        activeTab={activeTab.key}
        onChangeTab={handleChangeTab}
        viewAllHref={buildCollectionHref(slug, activeTab.gender)}
        viewAllLabel={viewAllLabel}
        gaTrackingPrefix={gaTrackingPrefix}
      />
      {!isFirstTab && isLoading ? (
        <ProductCarouselSkeleton />
      ) : (
        <ProductCarousel
          key={activeTab.key}
          products={products}
          gaTrackingValue={`${gaTrackingPrefix}__${activeTab.key}__product`}
        />
      )}
    </div>
  )
}
